/* eslint-disable spellcheck/spell-checker */
import { getDateTimeFromIso, getIsoStringFromDateTime } from './helpers';
import {
  GetFormsV2Response,
  FormStatuses,
  APSV2Form,
  GetFormsV2RequestParams,
} from '../../types';

/* 
Translate Cacahute API query params to satisfy APS query params
method name: getFieldReports
Cacahute API: /v8/project/${projectId}/field-report-types/${formTemplateId}/reports
APS API: /development/v2/projects/${projectId}/forms
*/
const translateQueryParams = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: Record<string, any>,
): GetFormsV2RequestParams => {
  // remove unused params
  delete params['inc_references'];
  delete params['include_pg_form_layout'];

  const apsParams: GetFormsV2RequestParams = {
    // add default params
    include: ['inactiveFormTemplates'],
    ...params,
  };

  // simple key mapping
  const keyMapping: Record<string, keyof GetFormsV2RequestParams> = {
    skip: 'offset',
    text_search: 'search',
    include_sublocations: 'include',
    include_editable_by_user: 'assignedToUser',
    report_date_min: 'formDateMin',
    report_date_max: 'formDateMax',
    due_date_min: 'dueDateMin',
    due_date_max: 'dueDateMax',
    location_ids: 'locationIds',
    approved_by_company_ids: 'approvedByCompanyIds',
    approved_by_role_ids: 'approvedByRoleIds',
    approved_by_user_ids: 'approvedByUserIds',
    template_types: 'templateTypes',
    assignee_company_ids: 'assigneeCompanyIds',
    assignee_user_ids: 'assigneeIds',
    assignee_role_ids: 'assigneeRoleIds',
    created_by_company_ids: 'createdByCompanyIds',
    created_by_role_ids: 'createdByRoleIds',
    created_by_user_ids: 'createdByUserIds',
    folder_uid: 'folderId',
  };

  type ValueMappingRecord = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [K in keyof GetFormsV2RequestParams]: (oldValue: any) => any;
  };
  // An object of value translation functions: key is the new query param key, the value is the translation function
  const valueMapping: ValueMappingRecord = {
    statuses: (oldValue: string[]) => {
      const statusMap: Record<string, FormStatuses> = {
        draft: 'inProgress',
        in_review: 'inReview',
        submitted: 'closed',
      };
      return oldValue.map((status: string) => statusMap[status] || status);
    },
    limit: (oldValue) => {
      return oldValue > 0 ? oldValue : null;
    },
    formDateMax: getIsoStringFromDateTime,
    formDateMin: getIsoStringFromDateTime,
    dueDateMax: getIsoStringFromDateTime,
    dueDateMin: getIsoStringFromDateTime,
    include: () => {
      return ['sublocations'];
    },
  };

  // translate keys/values
  Object.entries(apsParams).forEach(([key, value]) => {
    const newKey = keyMapping[key] || key;
    // remove old params from cacahute, type conversion is just here for typescript
    delete apsParams[key as unknown as keyof GetFormsV2RequestParams];

    const newValue = valueMapping[newKey]
      ? valueMapping[newKey]?.(value)
      : value;

    if (newValue !== null) {
      apsParams[newKey] = newValue;
    }
  });

  /* 
    sort replaces sort_order and sort_by
    combination of sortBy and sortOrder, in csv. Example: sort=formNum asc, updatedAt desc.
    */
  const translateSortOptions = () => {
    if (params['sort_by'] || params['sort_order']) {
      const sortBy = params['sort_by'].split(',');
      const sortOrder = params['sort_order'].split(',');

      const sortByMapping: Record<string, string> = {
        report_date: 'formDate',
        updated_at: 'updatedAt',
        report_num: 'formNum',
        due_date: 'dueDate',
        template_name: 'templateName',
        created_by_name: 'createdByName',
        updated_by_name: 'updatedByName',
      };

      // remove replaced keys
      delete apsParams['sort_by' as unknown as keyof GetFormsV2RequestParams];
      delete apsParams[
        'sort_order' as unknown as keyof GetFormsV2RequestParams
      ];

      const sortOptions = [];
      for (let i = 0; i < sortBy.length; i++) {
        const sort = sortByMapping[sortBy[i] as string] || sortBy[i];
        sortOptions.push(`${sort} ${sortOrder[i]}`);
      }
      apsParams['sort'] = sortOptions.toString();
    }
  };

  // handle special cases
  translateSortOptions();

  return apsParams;
};

// translate APS response to Cacahute, this should be only temporary until we fully migrate to APS
const translateResponse = (data: GetFormsV2Response) => {
  const mapResponseData = (forms: APSV2Form[]) => {
    type FormKeyMappingRecord = {
      [K in keyof APSV2Form]: string;
    };
    const formKeyMapping: FormKeyMappingRecord = {
      createdBy: 'created_by',
      createdAt: 'created_at',
      formDate: 'report_date',
      formTemplateId: 'type',
      id: 'uid',
      lastFetchedAt: 'last_fetched_at',
      updatedAt: 'updated_at',
      updatedBy: 'updated_by',
      userCreatedAt: 'user_created_at',
      nativeForm: 'pg_form',
      pdfFile: 'file',
      formNum: 'report_num',
      dueDate: 'due_date',
      locationId: 'location_id',
      assigneeId: 'assignee_id',
      assigneeType: 'assignee_type',
      description: 'title',
      lastSubmittedAt: 'last_submitted_at',
      lastSubmittedBy: 'last_submitted_by',
      lastReopenedBy: 'last_reopened_by',
    };

    // An object of value translation functions: key is the new query param key, the value is the translation function
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const valueMapping: Record<string, (apsValue: any) => any> = {
      status: (oldValue: FormStatuses) => {
        const statusMap: Record<FormStatuses, string> = {
          inProgress: 'draft',
          inReview: 'in_review',
          closed: 'submitted',
          archived: 'archived',
        };
        return statusMap[oldValue] || oldValue;
      },
      due_date: getDateTimeFromIso,
      created_at: getDateTimeFromIso,
      last_fetched_at: getDateTimeFromIso,
      report_date: getDateTimeFromIso,
      user_created_at: getDateTimeFromIso,
      updated_at: getDateTimeFromIso,
    };

    const cacaItems = forms.map((form) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cacaForm: Record<string, any> = {};
      // translate keys/values
      Object.entries(form).forEach(([key, value]) => {
        const newKey =
          formKeyMapping[key as unknown as keyof FormKeyMappingRecord] || key;
        const newValue = valueMapping[newKey]
          ? valueMapping[newKey]?.(value)
          : value;

        if (newValue !== null) {
          cacaForm[newKey] = newValue;
        }
      });

      return cacaForm;
    });

    return cacaItems;
  };

  return {
    total: data.pagination.totalResults,
    items: mapResponseData(data.data),
  };
};

const getFormsTransformers = {
  translateQueryParams,
  translateResponse,
};

export default getFormsTransformers;
